import React, { useMemo } from "react"
import { Pie } from "@nivo/pie"
import { ResponsiveWrapper } from "@nivo/core"
import type { TableModel } from "utils/tableModel"
import type { PieTile as PieTileDoc } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import {
  chartCellLeaves,
  modelToPieSlices,
  sortSlices,
} from "features/Dashboard/runtime/modelAdapters"
import { effectiveStatistic } from "features/Dashboard/runtime/tileView"
import {
  bottomLegends,
  CHART_INK,
  DIM_ALPHA,
  entityColours,
  formatValue,
  labelInk,
  LEGEND_ROW,
  legendLayout,
  nivoTheme,
  prefersReducedMotion,
  RIGHT_LEGEND,
  rightLegends,
  truncateLabel,
  withAlpha,
} from "./chartTheme"
import type { LegendItem } from "./chartTheme"
import ChartTooltip from "./ChartTooltip.component"
import ChartFrame from "./ChartFrame.component"
import { useCategoryCrossFilter } from "./useCategoryCrossFilter"
import { useTileTrace } from "./useTileTrace"
import type { TraceTarget } from "./useTileTrace"
import { markId, useChartTraceMenu } from "./useChartTraceMenu"

/**
 * A pie (or donut) tile: the table's row categories against its Totals
 * column, in table order or sorted by size, values on the slices when data
 * labels are on. Rows in another unit than the first are left out and
 * named under the pie. In View mode a slice click toggles a cross-filter on its
 * category and the other slices dim. `compact` draws one panel of a
 * repeated tile: no legend and no labels beside the slices (the panels
 * share one legend), small margins; `panel` names its category. In both
 * modes a right-click on a slice offers the trace of its number.
 */
const PieTile: React.FC<{
  tile: PieTileDoc
  model: TableModel
  /** The tile's last result, which the colours follow (the view by default). */
  raw?: TableModel
  compact?: boolean
  panel?: string
}> = ({ tile, model, raw = model, compact = false, panel }) => {
  const palette = useDashboardStore((s) => s.document.theme.chartPalette)
  const colourMap = useDashboardStore(
    (s) => s.document.theme.categoryColours
  )
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const v = tile.visual
  const sort = tile.chart.sort
  const statistic = effectiveStatistic(tile, controls)
  const { emits, selected, toggle } = useCategoryCrossFilter(tile, model, "down")
  const { openTrace, panel: tracePanel } = useTileTrace(tile, panel)
  const { openMenu, menu } = useChartTraceMenu(openTrace)
  const { slices, format, omitted, column } = useMemo(() => {
    const pie = modelToPieSlices(model, { statistic })
    return { ...pie, slices: sortSlices(pie.slices, sort) }
  }, [model, statistic, sort])
  const { measures, categories } = tile
  const colourOf = useMemo(
    () =>
      entityColours(raw, "down", palette, colourMap, {
        measures,
        labels: categories.labels,
      }),
    [raw, palette, colourMap, measures, categories.labels]
  )

  if (slices.length === 0) {
    return <p className="dq-tile__empty">No values to chart.</p>
  }

  const colourOfSlice = (id: string) => {
    const base = colourOf(id)
    return selected && !selected.has(id) ? withAlpha(base, DIM_ALPHA) : base
  }
  const legendItems: LegendItem[] = slices.map((s) => ({
    id: s.id,
    label: s.label,
    color: colourOf(s.id),
  }))
  const legend = compact ? "none" : v.legend

  /** The number of the slice the event is on, read from the pie's column. */
  const traceTarget = (event: React.MouseEvent): TraceTarget | null => {
    const id = markId(event, "arc.")
    const slice = slices.find((s) => s.id === id)
    if (!slice || column === null) return null
    const leaves = chartCellLeaves(model, {
      categoryAxis: "down",
      categoryId: slice.id,
      seriesId: column,
      statistic,
    })
    return leaves && { ...leaves, value: formatValue(slice.value, format) }
  }

  return (
    <ChartFrame
      kind="pie"
      clickable={emits}
      omitted={compact ? [] : omitted}
      onContextMenu={(e) => openMenu(e, traceTarget(e))}
      trace={
        <>
          {menu}
          {tracePanel}
        </>
      }
    >
      <ResponsiveWrapper>
        {({ width, height }) => {
          const bottomRows =
            legend === "bottom" ? legendLayout(width, legendItems).rows : 0
          const margin = compact
            ? { top: 6, right: 6, bottom: 6, left: 6 }
            : legend === "none"
              ? { top: 24, right: 88, bottom: 24, left: 88 }
              : {
                  top: 12,
                  right: legend === "right" ? RIGHT_LEGEND : 16,
                  bottom: legend === "bottom" ? 16 + bottomRows * LEGEND_ROW : 12,
                  left: 16,
                }
          const legends =
            legend === "right"
              ? rightLegends(legendItems)
              : legend === "bottom"
                ? bottomLegends(width, legendItems, margin.bottom)
                : []
          return (
            <Pie
              width={width}
              height={height}
              data={slices}
              theme={nivoTheme}
              colors={(d) => colourOfSlice(String(d.id))}
              innerRadius={v.donut ? 0.6 : 0}
              padAngle={1}
              cornerRadius={3}
              margin={margin}
              valueFormat={(n) => formatValue(n, format)}
              enableArcLabels={tile.chart.dataLabels === "on"}
              arcLabelsSkipAngle={12}
              arcLabelsTextColor={(d) => labelInk(d.color)}
              enableArcLinkLabels={legend === "none" && !compact}
              arcLinkLabel={(d) => truncateLabel(String(d.label), 14)}
              arcLinkLabelsSkipAngle={8}
              arcLinkLabelsDiagonalLength={12}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsTextColor={CHART_INK}
              arcLinkLabelsColor={{ from: "color" }}
              legends={legends}
              onClick={(d) => toggle(String(d.id))}
              tooltip={({ datum }) => (
                <ChartTooltip
                  title={String(datum.label)}
                  rows={[
                    {
                      key: String(datum.id),
                      colour: colourOf(String(datum.id)),
                      value: datum.formattedValue,
                    },
                  ]}
                />
              )}
              animate={!prefersReducedMotion()}
              role="img"
            />
          )
        }}
      </ResponsiveWrapper>
    </ChartFrame>
  )
}

export default PieTile
